// "Releases" on the project page (task 2026-10-05 "a hosted database publishes citable releases";
// backend/unio/routers/releases.py). A project admin presses Release on a HOSTED database and gets
// an immutable, numbered release: a pg_dump, one CSV per table with datapackage.json, the lineage
// tables and an RO-Crate, each file with its SHA-256. Every reader lists the releases, downloads
// one as a zip and copies its citation (publishing it is the department's repository, decided
// 2026-10-13, N1). Releases outlive hosting, so the panel shows while the project is hosted
// or has any release. While one runs, the list is polled. Columns marked personal data are left
// out of a release by default (O19: not in its CSVs, its dump or its change log), so every reader
// downloads it; the badge "without personal data" names them. A manager may choose an "internal
// release incl. personal data" instead: audited, it shows the badge "personal data" and its files
// are for project managers only (routers/releases.py).
import { useEffect, useState } from "react";
import { api } from "../api";
import { ErrorNote, Loading, SectionTitle, formatBytes } from "../components/Feedback";
import { Badge, Btn, Card, Field, Textarea } from "../components/Shared";
import { useApi } from "../useApi";

const MONO = { fontFamily: "'JetBrains Mono', monospace", overflowWrap: "anywhere" };
const STATUS_COLOR = { published: "green", running: "blue", failed: "red" };
const POLL_MS = 2000;

function ReleaseForm({ projectId, onDone, onCancel }) {
  const [notes, setNotes] = useState("");
  const [includePersonal, setIncludePersonal] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/releases`,
                     { notes: notes || null, include_personal: includePersonal });
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} aria-label="New release" style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
      <ErrorNote error={error} />
      <p style={{ margin: 0, fontSize: 13, color: "#374151" }}>
        A release freezes the database as it is now: a dump, every table as CSV with its data dictionary,
        the lineage of every row's source file, and an RO-Crate describing them. It never changes
        afterwards; data entry goes on meanwhile and lands in the next release.
      </p>
      <Field label="Notes" hint="Optional: what this release is for, e.g. the figures of a paper.">
        <Textarea value={notes} onChange={e => setNotes(e.target.value)} />
      </Field>
      <fieldset style={{ border: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        <legend style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Columns marked as personal data</legend>
        <label style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "flex-start" }}>
          <input type="radio" name="personal" checked={!includePersonal} onChange={() => setIncludePersonal(false)} />
          <span>
            Release without them (default): they are left out of the CSVs and the dump, and every
            project member may download the release.
          </span>
        </label>
        <label style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "flex-start" }}>
          <input type="radio" name="personal" checked={includePersonal} onChange={() => setIncludePersonal(true)} />
          <span>
            Internal release incl. personal data: every value is kept, the choice is recorded in the
            audit log, and only project managers may download its files.
          </span>
        </label>
      </fieldset>
      <div style={{ display: "flex", gap: 10 }}>
        <Btn type="submit" disabled={busy}>Publish release</Btn>
        <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
      </div>
    </form>
  );
}

function ReleaseCard({ projectId, slug, release: r }) {
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const get = async (path, filename) => {
    setError(null);
    try {
      await api.download(`/projects/${projectId}/releases/${r.number}/${path}`, filename);
    } catch (err) {
      setError(err);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(r.citation);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Card style={{ padding: "14px 20px" }}>
      <div id={`release-${r.number}`} data-testid={`release-${r.number}`}
           style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <strong style={{ fontSize: 15 }}>Release {r.number}</strong>
        <Badge color={STATUS_COLOR[r.status] || "gray"}>{r.status}</Badge>
        {(r.personal_data || []).length > 0 && (
          <Badge color="red" title={`Includes ${r.personal_data.join(", ")}; files for project managers only`}>
            personal data
          </Badge>
        )}
        {(r.omitted_columns || []).length > 0 && <Badge color="gray">without personal data</Badge>}
        <span style={{ fontSize: 13, color: "#4b5563" }}>
          design version {r.design_version}
          {r.finished_at ? ` · ${new Date(r.finished_at).toLocaleString()}` : ""}
          {r.created_by ? ` · by ${r.created_by}` : ""}
          {r.status === "published" ? ` · ${r.parts.length} files, ${formatBytes(r.total_bytes)}` : ""}
        </span>
      </div>
      {r.notes && <p style={{ margin: "6px 0 0", fontSize: 13, color: "#374151" }}>{r.notes}</p>}
      {(r.omitted_columns || []).length > 0 && (
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "#4b5563" }}>
          Left out (marked as personal data): {r.omitted_columns.join(", ")}
        </p>
      )}
      {r.status === "published" && (
        <>
          <div style={{ marginTop: 8, fontSize: 13 }}>
            <span style={{ color: "#4b5563" }}>Identifier: </span>
            <span style={MONO}>{r.identifier}</span>
          </div>
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, color: "#4b5563" }}>Cite as</div>
            <p data-testid={`citation-${r.number}`}
               style={{ margin: "4px 0 0", padding: "8px 10px", background: "#f3f4f6", borderRadius: 6, fontSize: 13 }}>
              {r.citation}
            </p>
          </div>
          <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Btn onClick={() => get("download", `${slug}-release-${r.number}.zip`)}>
              Download release {r.number}
            </Btn>
            <Btn variant="secondary" onClick={copy}>{copied ? "Citation copied" : "Copy citation"}</Btn>
          </div>
        </>
      )}
      {r.status === "failed" && (
        <pre style={{ ...MONO, fontSize: 12, whiteSpace: "pre-wrap", margin: "8px 0 0" }}>{r.log}</pre>
      )}
      <ErrorNote error={error} />
    </Card>
  );
}

export default function ReleasesPanel({ projectId, slug }) {
  const state = useApi(`/projects/${projectId}/releases`);
  const [creating, setCreating] = useState(false);
  const d = state.data;
  const running = !!d && d.releases.some(r => r.status === "running");
  const { reload } = state;

  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(reload, POLL_MS);
    return () => clearInterval(t);
  }, [running, reload]);

  if (state.error) return state.error.status === 404 ? null : <ErrorNote error={state.error} />;
  if (!d) return state.loading ? <Loading label="Loading releases…" /> : null;
  if (!d.hosted && d.releases.length === 0) return null;

  return (
    <section aria-labelledby="releases-title" style={{ marginBottom: 24 }}>
      <SectionTitle right={d.can_release && d.hosted && !creating && (
        <Btn onClick={() => setCreating(true)} disabled={running}>Release</Btn>
      )}>
        <span id="releases-title">Releases</span>
      </SectionTitle>
      {creating && (
        <Card style={{ marginBottom: 12 }}>
          <ReleaseForm projectId={projectId}
                       onDone={() => { setCreating(false); reload(); }} onCancel={() => setCreating(false)} />
        </Card>
      )}
      {d.releases.length === 0 ? (
        <p style={{ color: "#6b7280", fontSize: 14, margin: "8px 0" }}>
          No release yet. A release is a fixed, citable state of the hosted database.
        </p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 10 }}>
          {d.releases.map(r => (
            <ReleaseCard key={r.id} projectId={projectId} slug={slug} release={r} />
          ))}
        </div>
      )}
    </section>
  );
}
